import React, { useEffect, useState, useRef, useCallback } from "react";
import { ArrowLeft, ArrowDownLeft, ArrowUpRight, Smartphone, Building2, Home } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { fetchCustomerData } from "@/redux/slices/customerSlice";
import { getT } from "@/i18n/translations";


const PAGE_SIZE = 10;

const RedeemCash = () => {
  const t = getT();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: customer, loading } = useSelector((state) => state.customer);

  const [activeTab, setActiveTab] = useState("earned");
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loaderRef = useRef(null);

  useEffect(() => {
    const customerCode = localStorage.getItem("customercode");
    if (!customer) dispatch(fetchCustomerData(customerCode));
  }, []);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [activeTab]);

  const hasBankInfo = customer?.customer?.bankInfo && customer.customer.bankInfo.length > 0;
  const selectedBank = hasBankInfo ? customer.customer.bankInfo.find((b) => b.isDefault) : null;

  const availablePoints = customer?.walletsDetail?.availableValue || 0

  // customer?.WalletSummary
  //   ?.filter(item => item.type === "voucher" && item.VoucherUsed === false)
  //   ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalEarned =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher")
      ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const totalTransferred =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed === true)
      ?.reduce((sum, item) => sum + (item.earnedValue || 0), 0) || 0;

  const earnedTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher")
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: item.VoucherUsed,
        date: item.earnedDate,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const transferredTxs =
    customer?.WalletSummary
      ?.filter(item => item.type === "voucher" && item.VoucherUsed)
      ?.map(item => ({
        id: item.id,
        code: item.voucherCode,
        amount: item.earnedValue,
        isUsed: true,
        date: item.updatedDate,
      }))
      ?.sort((a, b) => new Date(b.date) - new Date(a.date)) || [];

  const activeTxs = activeTab === "earned" ? earnedTxs : transferredTxs;
  const visibleTxs = activeTxs.slice(0, visibleCount);
  const hasMore = visibleCount < activeTxs.length;

  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore) {
      setVisibleCount(prev => prev + PAGE_SIZE);
    }
  }, [hasMore]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  const handleRedeem = () => {
    if (!availablePoints || availablePoints <= 0) { toast.error(t.redeem_errNoBalance); return; }
    navigate("/bank-details");
  };

  const handleUPIRedeem = () => {
    if (!availablePoints || availablePoints <= 0) { toast.error(t.redeem_errNoBalance); return; }
    navigate("/upi-details", { state: { selectedAmount: availablePoints } });
  };

  return (
    <div
      className="min-h-screen flex flex-col items-center"
      style={{
        fontFamily: "'Poppins', sans-serif",
        backgroundColor: "#fff5f5",
        backgroundImage:
          "radial-gradient(circle, #ffd6d6 1px, transparent 1px)",
        backgroundSize: "22px 22px",
      }}
    >
      <div className="w-full max-w-md min-h-screen flex flex-col bg-[#ffe5e5]">

        {/* HEADER */}
        <div className="relative flex items-center justify-center px-4 py-4 mt-2">
          <button
            onClick={() => navigate(-1)}
            className="absolute left-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <ArrowLeft className="w-5 h-5 text-[#ED1C16]" />
          </button>

          <p className="text-[15px] font-semibold text-black">
            {t.redeem_pageTitle}
          </p>

          <button
            onClick={() => navigate("/home")}
            className="absolute right-4 w-9 h-9 flex items-center justify-center rounded-full bg-white shadow-sm border border-gray-100"
          >
            <Home className="w-5 h-5 text-[#ED1C16]" />
          </button>
        </div>

        <div className="px-4 pb-8 space-y-3 flex-1">

          {/* BALANCE CARD */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-gray-100 mx-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest text-center mb-1">
              {t.redeem_balance}
            </p>

            <p className="text-[48px] font-bold text-[#ED1C16] text-center">
              ₹{availablePoints}
            </p>
          </div>

          {/* TRANSFER FUNDS */}
          <div className="bg-white rounded-2xl px-5 py-5 shadow-sm border border-gray-100 mx-1">
            <p className="text-[15px] font-bold text-black mb-4">
              {t.redeem_transferFunds}
            </p>

            <div className="grid grid-cols-2 gap-3">

              {/* UPI */}
              <button
                onClick={handleUPIRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border border-gray-200 bg-gray-50 active:scale-95 transition"
              >
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <Smartphone size={20} className="text-[#ED1C16]" />
                </div>
                <p className="text-[13px] font-semibold text-black">
                  {t.redeem_upiId}
                </p>
              </button>

              {/* BANK */}
              <button
                onClick={handleRedeem}
                className="flex flex-col items-center justify-center gap-2 py-4 rounded-xl border border-gray-200 bg-gray-50 active:scale-95 transition"
              >
                <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
                  <Building2 size={20} className="text-[#ED1C16]" />
                </div>
                <p className="text-[13px] font-semibold text-black">
                  {t.redeem_bankAccount}
                </p>
              </button>

            </div>
          </div>

          {/* (OPTIONAL) TRANSACTIONS UI CAN ALSO BE REDESIGNED SAME WAY */}
        </div>

        {/* FOOTER */}
        {/* <div className="w-full py-3 border-t border-gray-200 mt-auto">
          <p className="text-[13px] text-black uppercase text-center tracking-wide">
            © {new Date().getFullYear()} Consumer Journeys. All Rights Reserved.
          </p>
        </div> */}

      </div>
    </div>
  );
};

export default RedeemCash;